import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import ToggleDarkMode from "./ToggleDarkMode";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import PageSelectorMenu from "./PageSelectorMenu";
import { ReactComponent as TimezoneIcon } from "../../assets/icons/timezoneIcon.svg";
import { selectUser } from "../../Redux/user/user";
import { useSelector } from "react-redux";
import {
  Bell,
  BellDot,
  ChevronLeft,
  ChevronRight,
  Dot,
  DotIcon,
  MessageSquareDot,
} from "lucide-react";
import {
  useFloating,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingFocusManager,
} from "@floating-ui/react";

import { darkTheme, lightTheme } from "../../theme/theme";
import { useUserNotificationsService } from "../../context/useUserNotificationsService";
import {
  format_time_to_timezone,
  same_time_in_timezone,
} from "../../helper/Generic/dateTimeFormat";
import WebSocketObserver from "../../helper/websocket/WebSocketObserver";
import { useWebSocketDataService } from "../../context/useWebSocketDataService";
import useSound from "use-sound";

const HeaderMenu = ({ theme, toggleTheme }) => {
  const ref = useRef(null);
  const notificationAudioRef = useRef(null);
  const navigate = useNavigate();
  const {
    setCupsSeasonsNotifications,
    getCupsAndSeasonsNotifications,
    cupsSeasonsNotifications,
    markAsReadNotification,
    setSelectedNotification,
  } = useUserNotificationsService();
  const { add_observer, remove_observer } = useWebSocketDataService();
  const [searchParams, setSearchParams] = useSearchParams();
  const [playOn] = useSound("/resources/sounds/notificationsound.wav", {
    interrupt: true,
    volume: 0.5,
  });
  const playOnRef = useRef(playOn);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = useState("Pricing");
  const [showDarkMode, setShowDarkMode] = useState(true);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [open, setOpen] = useState(false);

  const openMenu = Boolean(anchorEl);
  const location = useLocation();
  const user = useSelector(selectUser);

  // ✅ Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(8), flip(), shift()],
    placement: "bottom-end",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  // ✅ New API uses useInteractions()
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const notificationsObserver = new WebSocketObserver(
    "NotificationsChannel",
    (message) => {
      setCupsSeasonsNotifications((prev) => {
        // Copy the current notifications
        const updatedNotifications = [...prev.notifications];

        // Check if the new notification exists
        const index = updatedNotifications.findIndex(
          (notif) => notif.id === message.notification.id
        );

        if (index !== -1) {
          // Update existing notification
          updatedNotifications[index] = {
            ...updatedNotifications[index],
            ...message.notification,
          };
        } else {
          // Add new notification
          playOnRef.current?.();
          updatedNotifications.unshift(message.notification);
        }

        return {
          ...prev,
          unread_notifications: message.unread_notifications,
          notifications: updatedNotifications,
        };
      });
    }
  );

  const checkPage = () => {
    if (location.pathname.includes("betting")) {
      setPage("Betting");
    }
    if (location.pathname.includes("pricing")) {
      setPage("Pricing");
    }
    if (location.pathname.includes("leagues")) {
      setPage("Leagues");
    }
    if (location.pathname.includes("audit")) {
      setPage("Audit");
    }
    if (location.pathname.includes("admin")) {
      setPage("Admin");
    }
    if (location.pathname === "/") {
      setPage("Pricing");
    }
    if (location.pathname === "/userprofile") {
      setPage("Menu");
    }
  };

  const handleDarkMode = () => {
    if (location.pathname === "/pricing" || location.pathname === "/") {
      setShowDarkMode(true);
    } else {
      setShowDarkMode(false);
    }
  };

  const handleGetCupsAndSeasonsNotifications = (pageNum = 1) => {
    getCupsAndSeasonsNotifications(
      pageNum,
      () => {},
      (error) => console.error(error)
    );
  };

  const handlePrevPage = () => {
    if (notificationsPage > 1) {
      const newPage = notificationsPage - 1;
      setNotificationsPage(newPage);
      handleGetCupsAndSeasonsNotifications(newPage);
    }
  };

  const handleNextPage = () => {
    if (notificationsPage < cupsSeasonsNotifications?.pagination?.total_pages) {
      const newPage = notificationsPage + 1;
      setNotificationsPage(newPage);
      handleGetCupsAndSeasonsNotifications(newPage);
    }
  };

  const handleMatchPreviewButton = (notification, projectedLineup = false) => {
    const newParams = new URLSearchParams(searchParams);
    const fixtureStartTime = format_time_to_timezone(
      notification.fixture_date,
      user.timezone
    );

    const start_date = fixtureStartTime.startOf("date").toISOString();
    const end_date = fixtureStartTime.endOf("date").toISOString();

    if (notification.cup_id) {
      newParams.set("cupIds", notification.cup_id);
      newParams.delete("seasonIds");
    } else {
      newParams.set("seasonIds", notification.season_id);
      newParams.delete("cupIds");
      newParams.delete("selectedCupId");
    }

    newParams.delete("selectedLeagueForTeam");
    newParams.delete("leagueIds");
    newParams.delete("countryIds");
    newParams.set("start_date", start_date);
    newParams.set("end_date", end_date);
    newParams.set("fixtureId", notification.fixture_id);

    if (projectedLineup) {
      newParams.set("projected_lineup", "true");
    } else {
      newParams.delete("projected_lineup");
    }

    // Add a timestamp to force navigation reload if already on the page

    const searchString = newParams.toString();
    const path = "/leagues/match_preview";

    if (user.open_links_in_new_tabs) {
      window.open(`${path}?${searchString}`, "_blank", "noopener,noreferrer");
    } else {
      navigate(
        {
          pathname: path,
          search: searchString,
        },
        { replace: false }
      );
    }
    setSelectedNotification(notification);
    setOpen(false);
  };

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (openMenu && ref.current && !ref.current.contains(e.target)) {
        setAnchorEl(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [openMenu]);

  useEffect(() => {
    handleDarkMode();
    checkPage();
  }, [location]);

  useEffect(() => {
    handleGetCupsAndSeasonsNotifications(notificationsPage);
  }, []);

  useEffect(() => {
    playOnRef.current = playOn;
  }, [playOn]);

  useEffect(() => {
    add_observer(notificationsObserver);
    return () => {
      remove_observer(notificationsObserver);
    };
  }, []);

  return (
    <Container>
      {page !== "Menu" && (
        <ToggleDarkMode theme={theme} toggleTheme={toggleTheme} />
      )}
      <NotificationsCell
        ref={refs.setReference}
        onClick={() => setOpen((o) => !o)}
      >
        {cupsSeasonsNotifications.unread_notifications > 0 ? (
          <BellWrapper>
            <Bell />
            <DotWrapper>
              <DotIcon color="red" style={{ transform: "scale(2)" }} />
            </DotWrapper>
          </BellWrapper>
        ) : (
          <Bell />
        )}
      </NotificationsCell>

      {open && (
        <FloatingFocusManager context={context} modal={false}>
          <NotificationPopup
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <NotificationTitleWrapper>
              <h3>Notifications</h3>
              {cupsSeasonsNotifications?.unread_notifications > 0 && (
                <p>{cupsSeasonsNotifications?.unread_notifications} unread</p>
              )}
            </NotificationTitleWrapper>

            {cupsSeasonsNotifications?.notifications?.length ? (
              <ul>
                {cupsSeasonsNotifications?.notifications?.map(
                  (notificationItem, index) => {
                    const isUnread = notificationItem.read_at === null;
                    return (
                      <li
                        key={index}
                        onClick={() =>
                          handleMatchPreviewButton(notificationItem, true)
                        }
                      >
                        <NotificationItemWrapper unread={isUnread}>
                          <NotificationColumn>
                            <NotificationItemTitle>
                              {notificationItem.text}
                            </NotificationItemTitle>
                            <NotificationBottomLine>
                              <NotificaationItemDate>
                                {same_time_in_timezone(
                                  notificationItem.created_at,
                                  user.timezone
                                ).format("DD/MM/YYYY HH:mm")}
                              </NotificaationItemDate>
                            </NotificationBottomLine>
                          </NotificationColumn>
                        </NotificationItemWrapper>
                      </li>
                    );
                  }
                )}
              </ul>
            ) : (
              <NoNotifications>No notifications</NoNotifications>
            )}
            {cupsSeasonsNotifications?.notifications?.length > 0 && (
              <NotificationPopupPagination>
                <PaginationInfo>
                  Page {cupsSeasonsNotifications?.pagination?.page || 1} of{" "}
                  {cupsSeasonsNotifications?.pagination?.total_pages}
                </PaginationInfo>

                <ArrowsContainer>
                  <ArrowButton
                    onClick={handlePrevPage}
                    disabled={notificationsPage <= 1}
                  >
                    <ChevronLeft size={16} />
                  </ArrowButton>
                  <ArrowButton
                    onClick={handleNextPage}
                    disabled={
                      notificationsPage >=
                      (cupsSeasonsNotifications?.pagination?.total_pages || 1)
                    }
                  >
                    <ChevronRight size={16} />
                  </ArrowButton>
                </ArrowsContainer>
              </NotificationPopupPagination>
            )}
          </NotificationPopup>
        </FloatingFocusManager>
      )}
      <TimezoneCell>
        <TimezoneIcon />
        <h1>{user.timezone_name}</h1>
      </TimezoneCell>
      <div className="f-end">
        <PageSelectorMenu page={page} />
      </div>
      <UserSvg
        onClick={() => navigate("/userprofile")}
        style={{ cursor: "pointer" }}
        color={
          location.pathname === "/userprofile" ? "var(--primaryBlue)" : "white"
        }
      />
    </Container>
  );
};

export default HeaderMenu;
const Container = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  column-gap: 20px;

  img {
    cursor: pointer;
    height: 100%;
  }
  .f-end {
    display: flex;
    justify-content: end;
    align-items: center;
    position: relative;
  }
`;

const TimezoneCell = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 20px;
  column-gap: 5px;
  background: #263549;
  border-radius: 20px;
  h1 {
    font-family: "Roboto";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;

    color: #ffffff;
  }
`;

const NotificationsCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  color: ${lightTheme.menuBgColor};
`;
const NotificationPopup = styled.div`
  display: flex;
  flex-direction: column;
  background: #1b2533;
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
  min-width: 260px;
  max-width: 320px;
  max-height: calc(100vh - 100px); /* stay within viewport height */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  overflow: hidden; /* prevent content from overflowing popup borders */

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    flex: 1; /* fill space above pagination */
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }

  li {
    font-size: 13px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  li:last-child {
    border-bottom: none;
  }

  /* custom scrollbar for WebKit browsers */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Smoothly adapt on resize */
  transition: max-height 0.2s ease-in-out;
`;

const NotificationPopupPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const PaginationInfo = styled.p`
  font-size: 12px;
  color: #bbb;
  margin: 0;
`;

const ArrowsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ArrowButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 3px 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const NoNotifications = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 13px;
  padding: 20px;
  text-align: center;
`;

const NotificationItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: 400;
  padding: 8px;
  transition: background 0.2s;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  background: ${({ unread, theme }) => (unread ? "#4174B2" : "transparent")};
`;

const NotificationLine = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
`;
const NotificationColumn = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 10px;
`;
const NotificationItemTitle = styled.p`
  font-size: 14px;
  width: 100%;
`;

const NotificaationItemDate = styled.p`
  font-size: 12px;
  color: ${lightTheme.squadManager.noStChanceColor};
`;
const NotificationBottomLine = styled.div`
  display: flex;
  align-items: center;
`;

const NotificationTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 0;

  h3 {
    font-size: 18px;
    font-weight: 400;
    padding-bottom: 4px;
    flex-shrink: 0; /* prevents shrinking on resize */
  }

  p {
    margin-left: auto;
    font-size: 14px;
    color: #aaa;
  }
`;
const BellWrapper = styled.div`
  display: flex;
  position: relative;
`;
const DotWrapper = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  justify-conten: center;
  top: -10px;
  right: -7px;
`;
